import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';
import { Repeat, DollarSign, TrendingUp, Calendar, Download, RefreshCw } from 'lucide-react';
import { useSession } from '@clerk/clerk-react';
import DetailedAnalyticsService from '../../../services/dashboard/detailedAnalyticsService';

const SubscriptionsAnalysis = ({ companyId }) => {
  const { session } = useSession();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(30);
  const [selectedStatus, setSelectedStatus] = useState('all');

  useEffect(() => {
    if (session) {
      loadSubscriptionsData();
    }
  }, [companyId, dateRange, session]);

  const loadSubscriptionsData = async () => {
    if (!session) return;
    
    setLoading(true);
    try {
      const analyticsService = new DetailedAnalyticsService(session);
      const subscriptionsData = await analyticsService.getSubscriptionsData(companyId, dateRange);
      setData(subscriptionsData);
    } catch (error) {
      console.error('Error loading subscriptions data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'active': '#158B5C',
      'inactive': '#f59e0b',
      'cancelled': '#ef4444',
      'expired': '#6b7280',
      'trial': '#3b82f6'
    };
    return colors[status] || '#6b7280';
  };

  const getStatusBadgeVariant = (status) => {
    const variants = {
      'active': 'default',
      'inactive': 'default',
      'cancelled': 'destructive',
      'expired': 'secondary',
      'trial': 'default'
    };
    return variants[status] || 'secondary';
  };

  const getBillingFrequencyColor = (frequency) => {
    const colors = {
      'monthly': '#3b82f6',
      'quarterly': '#158B5C',
      'yearly': '#f59e0b',
      'one-time': '#8b5cf6'
    };
    return colors[frequency] || '#6b7280';
  };

  const filteredSubscriptions = selectedStatus === 'all' 
    ? data?.subscriptions || []
    : (data?.subscriptions || []).filter(subscription => subscription.status === selectedStatus);

  const statusData = data?.summary?.statusCounts ? Object.entries(data.summary.statusCounts).map(([status, count]) => ({
    status,
    count,
    percentage: data.summary.totalSubscriptions > 0 ? (count / data.summary.totalSubscriptions) * 100 : 0
  })) : [];

  const billingFrequencyData = filteredSubscriptions.reduce((acc, sub) => {
    const freq = sub.billing_frequency || 'monthly';
    acc[freq] = (acc[freq] || 0) + 1;
    return acc;
  }, {});

  const billingFrequencyChartData = Object.entries(billingFrequencyData).map(([frequency, count]) => ({
    frequency,
    count
  }));

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-8 bg-gray-200 rounded w-1/2"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Controles */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex gap-2">
          <Select value={dateRange.toString()} onValueChange={(value) => setDateRange(parseInt(value))}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Período" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Últimos 7 días</SelectItem>
              <SelectItem value="30">Últimos 30 días</SelectItem>
              <SelectItem value="90">Últimos 90 días</SelectItem>
            </SelectContent>
          </Select>
          
          <Select value={selectedStatus} onValueChange={setSelectedStatus}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estados</SelectItem>
              {Object.keys(data?.summary?.statusCounts || {}).map(status => (
                <SelectItem key={status} value={status}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Button variant="outline" size="sm">
          <Download className="h-4 w-4 mr-2" />
          Exportar
        </Button>
      </div>

      {/* Métricas principales */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Suscripciones</p>
                <p className="text-2xl font-bold">{data?.summary?.totalSubscriptions || 0}</p>
              </div>
              <Repeat className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Activas</p>
                <p className="text-2xl font-bold">{data?.summary?.activeSubscriptions || 0}</p>
              </div>
              <RefreshCw className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Ingresos Totales</p>
                <p className="text-2xl font-bold">${data?.summary?.totalRevenue?.toLocaleString() || 0}</p>
              </div>
              <DollarSign className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Ingreso Promedio</p>
                <p className="text-2xl font-bold">${data?.summary?.avgRevenue?.toLocaleString() || 0}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Gráficos y análisis */}
      <Tabs defaultValue="trends" className="space-y-4">
        <TabsList>
          <TabsTrigger value="trends">Tendencias</TabsTrigger>
          <TabsTrigger value="status">Estados</TabsTrigger>
          <TabsTrigger value="billing">Facturación</TabsTrigger>
          <TabsTrigger value="details">Detalles</TabsTrigger>
        </TabsList>

        <TabsContent value="trends" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Tendencia de Suscripciones</CardTitle>
              <CardDescription>
                Evolución diaria de suscripciones e ingresos en los últimos {dateRange} días
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={data?.chartData || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip />
                  <Line 
                    yAxisId="left"
                    type="monotone" 
                    dataKey="count" 
                    stroke="#3b82f6" 
                    strokeWidth={2}
                    name="Cantidad"
                  />
                  <Line 
                    yAxisId="right"
                    type="monotone" 
                    dataKey="revenue" 
                    stroke="#158B5C" 
                    strokeWidth={2}
                    name="Ingresos"
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="status" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Distribución por Estado</CardTitle>
                <CardDescription>Suscripciones por estado</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={statusData}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="count"
                      label={({ status, count }) => `${status}: ${count}`}
                    >
                      {statusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={getStatusColor(entry.status)} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Estados - Resumen</CardTitle>
                <CardDescription>Cantidad y porcentaje por estado</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {statusData.map((item) => (
                    <div key={item.status} className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: getStatusColor(item.status) }}
                        />
                        <span className="text-sm font-medium">
                          {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                        </span>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-bold">{item.count}</div>
                        <div className="text-xs text-muted-foreground">
                          {item.percentage.toFixed(1)}%
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="billing" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Frecuencia de Facturación</CardTitle>
              <CardDescription>Distribución por tipo de facturación</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={billingFrequencyChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="frequency" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="#3b82f6" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="details" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Detalle de Suscripciones</CardTitle>
              <CardDescription>
                Lista detallada de suscripciones {selectedStatus !== 'all' && `con estado ${selectedStatus}`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2">Plan</th>
                      <th className="text-left p-2">Monto</th>
                      <th className="text-left p-2">Frecuencia</th>
                      <th className="text-left p-2">Inicio</th>
                      <th className="text-left p-2">Próximo Pago</th>
                      <th className="text-left p-2">Estado</th>
                      <th className="text-left p-2">Auto-renovación</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSubscriptions.slice(0, 50).map((subscription) => (
                      <tr key={subscription.id} className="border-b hover:bg-gray-50">
                        <td className="p-2">
                          <span className="font-medium">{subscription.plan_name || 'N/A'}</span>
                        </td>
                        <td className="p-2">
                          <span className="font-mono">
                            ${subscription.amount?.toLocaleString() || 0} {subscription.currency || 'CLP'}
                          </span>
                        </td>
                        <td className="p-2">
                          <Badge 
                            variant="outline" 
                            style={{ borderColor: getBillingFrequencyColor(subscription.billing_frequency) }}
                          >
                            {subscription.billing_frequency || 'monthly'}
                          </Badge>
                        </td>
                        <td className="p-2">
                          {subscription.start_date ? (
                            <div className="flex items-center space-x-1">
                              <Calendar className="h-3 w-3" />
                              <span>{new Date(subscription.start_date).toLocaleDateString('es-CL')}</span>
                            </div>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </td>
                        <td className="p-2">
                          {subscription.next_billing_date ? (
                            <div className="flex items-center space-x-1">
                              <Calendar className="h-3 w-3" />
                              <span>{new Date(subscription.next_billing_date).toLocaleDateString('es-CL')}</span>
                            </div>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </td>
                        <td className="p-2">
                          <Badge 
                            variant={getStatusBadgeVariant(subscription.status)}
                            style={{ 
                              backgroundColor: getStatusColor(subscription.status) + '20',
                              borderColor: getStatusColor(subscription.status),
                              color: getStatusColor(subscription.status)
                            }}
                          >
                            {subscription.status?.charAt(0).toUpperCase() + subscription.status?.slice(1)}
                          </Badge>
                        </td>
                        <td className="p-2">
                          {subscription.auto_renew ? (
                            <Badge variant="default" className="bg-green-100 text-green-800">
                              Sí
                            </Badge>
                          ) : (
                            <Badge variant="secondary">No</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {filteredSubscriptions.length > 50 && (
                <div className="mt-4 text-center text-sm text-muted-foreground">
                  Mostrando 50 de {filteredSubscriptions.length} suscripciones
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SubscriptionsAnalysis;